import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import AlarmTwoToneIcon from "@mui/icons-material/AlarmTwoTone";
import { Task, Tag, User, RoomStatus, Reminder } from "../types/types.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorBg,
  PaletteColorDarkBg,
} from "../types/PaletteColor.ts";
import { TaskUpdateDto } from "../hooks/useTodos.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface ReminderManagementDialogProps {
  tasks: Task[];
  onClose: () => void;
  roomMembers: User[];
  availableTags: Tag[];
  color: PaletteColor;
  honbuId: number;
  roomId: number;
  onUpdateTask: (taskId: string, updates: TaskUpdateDto) => Promise<void>;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  onDeleteTask: (taskId: string) => Promise<void>;
  addReminder: (
    taskId: string,
    reminderDateTime: Date
  ) => Promise<Reminder | null>;
  deleteReminder: (reminderId: string | number) => Promise<boolean>;
  onManageTags: () => void;
  roomStatuses: RoomStatus[];
  forceReload: () => void;
  isMobile?: boolean;
  currentTodoListName?: string;
}

// ステータスに基づく色を取得
const getStatusColor = (
  status: string | undefined,
  roomStatuses: RoomStatus[]
): string => {
  const statusConfig = roomStatuses.find((s) => s.statusName === status);
  if (statusConfig) {
    const colorMap: Record<string, string> = {
      Gray: "#9CA3AF",
      Blue: "#3B82F6",
      Green: "#22C55E",
      Yellow: "#EAB308",
      Red: "#EF4444",
      Purple: "#A855F7",
      Pink: "#EC4899",
      Orange: "#F97316",
    };
    return colorMap[statusConfig.color] || "#9CA3AF";
  }
  switch (status) {
    case "未対応":
      return "#9CA3AF";
    case "対応中":
      return "#3B82F6";
    case "完了":
      return "#22C55E";
    case "非表示":
      return "#A855F7";
    default:
      return "#9CA3AF";
  }
};

interface ProjectWithReminders {
  todoListId: number;
  todoListName: string;
  tasks: TaskWithReminders[];
}

interface TaskWithReminders {
  id: string;
  title: string;
  status: string;
  reminders: Reminder[];
}

// リマインダー編集行コンポーネント
const ReminderRow = ({
  reminder,
  onDelete,
  onUpdate,
  isDarkMode,
  textColor,
}: {
  reminder: Reminder;
  onDelete: (reminderId: string) => void;
  onUpdate: (reminderId: string, newDateTime: Date) => void;
  isDarkMode: boolean;
  textColor: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(() => {
    const dt = new Date(reminder.reminderDateTime);
    // ローカルタイムでフォーマット
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const hours = String(dt.getHours()).padStart(2, "0");
    const minutes = String(dt.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSave = () => {
    const newDate = new Date(editValue);
    if (!isNaN(newDate.getTime())) {
      onUpdate(reminder.reminderId, newDate);
    }
    setIsEditing(false);
  };

  const isPast = new Date(reminder.reminderDateTime) < new Date();

  return (
    <div className={`flex items-center gap-2 ${isPast ? "opacity-50" : ""}`}>
      <AlarmTwoToneIcon
        fontSize="small"
        style={{ color: textColor }}
        className="flex-shrink-0"
      />
      {isEditing ? (
        <div className="flex items-center gap-2 flex-1">
          <input
            type="datetime-local"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`flex-1 px-2 py-1 rounded border text-sm ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white"
                : "bg-white border-gray-300"
            }`}
          />
          <button
            onClick={handleSave}
            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            保存
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className={`px-2 py-1 text-xs rounded ${
              isDarkMode
                ? "bg-gray-600 text-white hover:bg-gray-500"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            キャンセル
          </button>
        </div>
      ) : (
        <>
          {reminder.isAutoGenerated && (
            <span
              className="text-[10px] leading-none px-1.5 py-0.5 rounded border font-medium flex-shrink-0"
              style={{ color: textColor, borderColor: textColor }}
              title="プロジェクトの自動リマインダー設定で作成されました"
            >
              自動
            </span>
          )}
          <span
            className={`flex-1 text-sm cursor-pointer hover:underline ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
            onClick={() => setIsEditing(true)}
          >
            {formatDateTime(reminder.reminderDateTime)}
            {isPast && " (期限切れ)"}
          </span>
          <button
            onClick={() => onDelete(reminder.reminderId)}
            className={`p-1 rounded hover:bg-red-100 ${
              isDarkMode ? "hover:bg-red-900" : ""
            }`}
            title="削除"
          >
            <DeleteIcon fontSize="small" className="text-red-500" />
          </button>
        </>
      )}
    </div>
  );
};

// 新規リマインダー追加コンポーネント
const AddReminderRow = ({
  onAdd,
  isDarkMode,
}: {
  onAdd: (dateTime: Date) => void;
  isDarkMode: boolean;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });

  const handleAdd = () => {
    const newDate = new Date(newValue);
    if (!isNaN(newDate.getTime())) {
      onAdd(newDate);
      setIsAdding(false);
      // リセット
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      setNewValue(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  };

  if (!isAdding) {
    return (
      <button
        onClick={() => setIsAdding(true)}
        className={`flex items-center gap-1 text-sm py-1 ${
          isDarkMode
            ? "text-blue-400 hover:text-blue-300"
            : "text-blue-600 hover:text-blue-700"
        }`}
      >
        <AddIcon fontSize="small" />
        リマインダーを追加
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 py-1">
      <AddIcon fontSize="small" className="text-blue-500 flex-shrink-0" />
      <input
        type="datetime-local"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        className={`flex-1 px-2 py-1 rounded border text-sm ${
          isDarkMode
            ? "bg-gray-700 border-gray-600 text-white"
            : "bg-white border-gray-300"
        }`}
      />
      <button
        onClick={handleAdd}
        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        追加
      </button>
      <button
        onClick={() => setIsAdding(false)}
        className={`px-2 py-1 text-xs rounded ${
          isDarkMode
            ? "bg-gray-600 text-white hover:bg-gray-500"
            : "bg-gray-200 hover:bg-gray-300"
        }`}
      >
        キャンセル
      </button>
    </div>
  );
};

// タスクとリマインダーのカードコンポーネント
const TaskReminderCard = ({
  task,
  isDarkMode,
  textColor,
  statusColor,
  onDeleteReminder,
  onUpdateReminder,
  onAddReminder,
}: {
  task: TaskWithReminders;
  isDarkMode: boolean;
  textColor: string;
  statusColor: string;
  onDeleteReminder: (taskId: string, reminderId: string) => void;
  onUpdateReminder: (
    taskId: string,
    reminderId: string,
    newDateTime: Date
  ) => void;
  onAddReminder: (taskId: string, dateTime: Date) => void;
}) => {
  // 未来のリマインダーのみフィルタ
  const now = new Date();
  const futureReminders = task.reminders.filter(
    (r) => new Date(r.reminderDateTime) > now
  );
  const pastReminders = task.reminders.filter(
    (r) => new Date(r.reminderDateTime) <= now
  );

  return (
    <div
      className={`rounded-lg border mb-2 overflow-hidden ${
        isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {/* タスクタイトル */}
      <div className="flex items-center">
        <div
          className="w-1 self-stretch flex-shrink-0"
          style={{ backgroundColor: statusColor }}
        />
        <div className="flex-1 p-3">
          <div
            className={`font-medium text-sm ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {task.title}
          </div>
          {/* リマインダー一覧 */}
          <div className="mt-2 space-y-1">
            {futureReminders.map((reminder) => (
              <ReminderRow
                key={reminder.reminderId}
                reminder={reminder}
                onDelete={(reminderId) => onDeleteReminder(task.id, reminderId)}
                onUpdate={(reminderId, newDateTime) =>
                  onUpdateReminder(task.id, reminderId, newDateTime)
                }
                isDarkMode={isDarkMode}
                textColor={textColor}
              />
            ))}
            {pastReminders.length > 0 && (
              <details className="mt-1">
                <summary
                  className={`text-xs cursor-pointer ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  過去のリマインダー ({pastReminders.length}件)
                </summary>
                <div className="mt-1 pl-2">
                  {pastReminders.map((reminder) => (
                    <ReminderRow
                      key={reminder.reminderId}
                      reminder={reminder}
                      onDelete={(reminderId) =>
                        onDeleteReminder(task.id, reminderId)
                      }
                      onUpdate={(reminderId, newDateTime) =>
                        onUpdateReminder(task.id, reminderId, newDateTime)
                      }
                      isDarkMode={isDarkMode}
                      textColor={textColor}
                    />
                  ))}
                </div>
              </details>
            )}
            {/* リマインダー追加 */}
            <AddReminderRow
              onAdd={(dateTime) => onAddReminder(task.id, dateTime)}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export function ReminderManagementDialog({
  tasks,
  onClose,
  roomMembers,
  availableTags,
  color,
  honbuId,
  roomId,
  onUpdateTask,
  onUpdateStatus,
  onDeleteTask,
  addReminder,
  deleteReminder,
  onManageTags,
  roomStatuses,
  forceReload,
  isMobile = false,
  currentTodoListName = "現在のプロジェクト",
}: ReminderManagementDialogProps) {
  const isDarkMode = useDarkMode();

  const textColor = isDarkMode ? PaletteColorBg[color] : PaletteColorHex[color];

  // 現在のプロジェクトのタスクIDセット
  const currentProjectTaskIds = useMemo(() => {
    const ids = new Set<string>();
    const collectIds = (taskList: Task[]) => {
      for (const task of taskList) {
        ids.add(task.id);
        if (task.childTasks) collectIds(task.childTasks);
      }
    };
    collectIds(tasks);
    return ids;
  }, [tasks]);

  // 選択中のタスクが現在のプロジェクトかどうか
  const isCurrentProjectTask = useCallback(
    (taskId: string) => currentProjectTaskIds.has(taskId),
    [currentProjectTaskIds]
  );

  // 現在のプロジェクトのリマインダー付きタスク
  const currentProjectTasks = useMemo((): TaskWithReminders[] => {
    const now = new Date();
    const collectTasks = (taskList: Task[]): TaskWithReminders[] => {
      const result: TaskWithReminders[] = [];
      for (const task of taskList) {
        if (task.reminders && task.reminders.length > 0) {
          // 未来のリマインダーが1件以上あるかチェック
          const hasFutureReminders = task.reminders.some(
            (r) => new Date(r.reminderDateTime) > now
          );
          if (hasFutureReminders) {
            result.push({
              id: task.id,
              title: task.title,
              status: task.status,
              reminders: task.reminders,
            });
          }
        }
        if (task.childTasks && task.childTasks.length > 0) {
          result.push(...collectTasks(task.childTasks));
        }
      }
      return result;
    };
    return collectTasks(tasks);
  }, [tasks]);

  // 他のプロジェクト用の状態
  const [otherProjects, setOtherProjects] = useState<ProjectWithReminders[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    new Set([-1]) // -1は現在のプロジェクト用
  );

  // 他のプロジェクトのデータを取得
  const fetchOtherProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=true`
      );
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();

      const projectsWithReminders: ProjectWithReminders[] = [];
      const now = new Date();

      for (const list of data.Lists || data.lists || []) {
        const collectTasksWithReminders = (
          taskList: any[]
        ): TaskWithReminders[] => {
          const result: TaskWithReminders[] = [];
          for (const task of taskList) {
            const reminders = (task.Reminders || task.reminders || []).map(
              (r: any) => ({
                reminderId: (r.ReminderId || r.reminderId).toString(),
                taskId: (r.TaskId || r.taskId).toString(),
                reminderDateTime: r.ReminderDateTime || r.reminderDateTime,
              })
            );
            if (reminders.length > 0) {
              // 未来のリマインダーが1件以上あるかチェック
              const hasFutureReminders = reminders.some(
                (r: Reminder) => new Date(r.reminderDateTime) > now
              );
              if (hasFutureReminders) {
                const taskId = task.taskId?.toString() || task.id;
                // 現在のプロジェクトのタスクは除外
                if (!currentProjectTaskIds.has(taskId)) {
                  result.push({
                    id: taskId,
                    title: task.title,
                    status: task.status,
                    reminders,
                  });
                }
              }
            }
            if (task.childTasks && task.childTasks.length > 0) {
              result.push(...collectTasksWithReminders(task.childTasks));
            }
          }
          return result;
        };

        const tasksWithRem = collectTasksWithReminders(list.tasks || []);

        if (tasksWithRem.length > 0) {
          projectsWithReminders.push({
            todoListId: list.todoListId,
            todoListName: list.name,
            tasks: tasksWithRem,
          });
        }
      }

      setOtherProjects(projectsWithReminders);
    } catch (error) {
      console.error("Failed to fetch other projects:", error);
    } finally {
      setIsLoading(false);
    }
  }, [roomId, honbuId, currentProjectTaskIds]);

  // 初回読み込み
  useEffect(() => {
    fetchOtherProjects();
  }, [fetchOtherProjects]);

  const toggleProjectExpand = (projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // リマインダー削除ハンドラ
  const handleDeleteReminder = useCallback(
    async (taskId: string, reminderId: string) => {
      if (isCurrentProjectTask(taskId)) {
        // 現在のプロジェクトの場合
        await deleteReminder(reminderId);
      } else {
        // 他のプロジェクトの場合
        const response = await fetch(`/api/reminders/${reminderId}`, {
          method: "DELETE",
        });
        if (response.ok) {
          // ローカルステートを更新
          setOtherProjects((prev) =>
            prev
              .map((project) => ({
                ...project,
                tasks: project.tasks
                  .map((task) =>
                    task.id === taskId
                      ? {
                          ...task,
                          reminders: task.reminders.filter(
                            (r) => r.reminderId !== reminderId
                          ),
                        }
                      : task
                  )
                  .filter((task) => task.reminders.length > 0),
              }))
              .filter((project) => project.tasks.length > 0)
          );
        }
      }
    },
    [isCurrentProjectTask, deleteReminder]
  );

  // リマインダー更新ハンドラ (削除して追加)
  const handleUpdateReminder = useCallback(
    async (taskId: string, reminderId: string, newDateTime: Date) => {
      // まず削除
      let deleteSuccess = false;
      if (isCurrentProjectTask(taskId)) {
        deleteSuccess = await deleteReminder(reminderId);
      } else {
        const response = await fetch(`/api/reminders/${reminderId}`, {
          method: "DELETE",
        });
        deleteSuccess = response.ok;
      }

      if (!deleteSuccess) return;

      // 新しいリマインダーを追加
      if (isCurrentProjectTask(taskId)) {
        await addReminder(taskId, newDateTime);
      } else {
        const response = await fetch(`/api/tasks/${taskId}/reminders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reminderDateTime: newDateTime.toISOString(),
          }),
        });
        if (response.ok) {
          const newReminder = await response.json();
          const reminder: Reminder = {
            reminderId: newReminder.reminderId.toString(),
            taskId: newReminder.taskId.toString(),
            reminderDateTime: newReminder.reminderDateTime,
          };
          // ローカルステートを更新
          setOtherProjects((prev) =>
            prev.map((project) => ({
              ...project,
              tasks: project.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      reminders: [
                        ...task.reminders.filter(
                          (r) => r.reminderId !== reminderId
                        ),
                        reminder,
                      ],
                    }
                  : task
              ),
            }))
          );
        }
      }
    },
    [isCurrentProjectTask, deleteReminder, addReminder]
  );

  // リマインダー追加ハンドラ
  const handleAddReminder = useCallback(
    async (taskId: string, dateTime: Date) => {
      if (isCurrentProjectTask(taskId)) {
        await addReminder(taskId, dateTime);
      } else {
        const response = await fetch(`/api/tasks/${taskId}/reminders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reminderDateTime: dateTime.toISOString(),
          }),
        });
        if (response.ok) {
          const newReminder = await response.json();
          const reminder: Reminder = {
            reminderId: newReminder.reminderId.toString(),
            taskId: newReminder.taskId.toString(),
            reminderDateTime: newReminder.reminderDateTime,
          };
          // ローカルステートを更新
          setOtherProjects((prev) =>
            prev.map((project) => ({
              ...project,
              tasks: project.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      reminders: [...task.reminders, reminder],
                    }
                  : task
              ),
            }))
          );
        }
      }
    },
    [isCurrentProjectTask, addReminder]
  );

  // プロジェクトセクションコンポーネント
  const ProjectSection = ({
    projectId,
    projectName,
    tasks,
    isExpanded,
    onToggle,
  }: {
    projectId: number;
    projectName: string;
    tasks: TaskWithReminders[];
    isExpanded: boolean;
    onToggle: () => void;
  }) => {
    const taskCount = tasks.length;
    const reminderCount = tasks.reduce((sum, t) => sum + t.reminders.length, 0);

    return (
      <div
        className={`mb-3 rounded-lg overflow-hidden ${
          isDarkMode ? "bg-gray-800" : "bg-gray-50"
        }`}
      >
        <button
          onClick={onToggle}
          className={`w-full flex items-center gap-2 p-3 text-left ${
            isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
          }`}
        >
          {isExpanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
          <span
            className={`font-medium flex-1 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {projectName}
          </span>
          <span
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {taskCount}タスク・{reminderCount}件
          </span>
        </button>
        {isExpanded && (
          <div className="px-3 pb-3">
            {tasks.map((task) => (
              <TaskReminderCard
                key={task.id}
                task={task}
                isDarkMode={isDarkMode}
                textColor={textColor}
                statusColor={getStatusColor(task.status, roomStatuses)}
                onDeleteReminder={handleDeleteReminder}
                onUpdateReminder={handleUpdateReminder}
                onAddReminder={handleAddReminder}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // 合計カウント
  const totalTasks =
    currentProjectTasks.length +
    otherProjects.reduce((sum, p) => sum + p.tasks.length, 0);
  const totalReminders =
    currentProjectTasks.reduce((sum, t) => sum + t.reminders.length, 0) +
    otherProjects.reduce(
      (sum, p) => sum + p.tasks.reduce((s, t) => s + t.reminders.length, 0),
      0
    );

  // モバイル版
  if (isMobile) {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col ${
          isDarkMode ? "bg-gray-900" : "bg-white"
        }`}
      >
        {/* ヘッダー */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : ""
          }`}
        >
          <h3
            className={`font-bold text-lg flex items-center gap-2 ${
              isDarkMode ? "text-white" : ""
            }`}
          >
            <AlarmTwoToneIcon style={{ color: textColor }} />
            リマインダー管理
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <CloseIcon className={isDarkMode ? "text-white" : ""} />
          </button>
        </div>

        {/* サマリー */}
        <div
          className={`px-4 py-2 text-sm ${
            isDarkMode
              ? "text-gray-400 bg-gray-800"
              : "text-gray-500 bg-gray-50"
          }`}
        >
          {totalTasks}タスク・{totalReminders}件のリマインダー
        </div>

        {/* メインコンテンツ */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div
              className={`p-8 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              読み込み中...
            </div>
          ) : totalTasks === 0 ? (
            <div
              className={`p-8 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              リマインダーが設定されているタスクはありません
            </div>
          ) : (
            <>
              {/* 現在のプロジェクト */}
              {currentProjectTasks.length > 0 && (
                <ProjectSection
                  projectId={-1}
                  projectName={currentTodoListName}
                  tasks={currentProjectTasks}
                  isExpanded={expandedProjects.has(-1)}
                  onToggle={() => toggleProjectExpand(-1)}
                />
              )}

              {/* 他のプロジェクト */}
              {otherProjects.map((project) => (
                <ProjectSection
                  key={project.todoListId}
                  projectId={project.todoListId}
                  projectName={project.todoListName}
                  tasks={project.tasks}
                  isExpanded={expandedProjects.has(project.todoListId)}
                  onToggle={() => toggleProjectExpand(project.todoListId)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  // PC版
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex justify-end"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className={`w-full md:w-[450px] h-full shadow-lg animate-slide-in-right flex flex-col ${
          isDarkMode ? "bg-gray-900" : "bg-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className={`flex-shrink-0 p-4 border-b flex justify-between items-center ${
            isDarkMode ? "border-gray-700" : ""
          }`}
        >
          <h3
            className={`font-bold flex items-center gap-2 ${
              isDarkMode ? "text-white" : ""
            }`}
          >
            <AlarmTwoToneIcon style={{ color: textColor }} />
            リマインダー管理
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <CloseIcon className={isDarkMode ? "text-white" : ""} />
          </button>
        </div>

        {/* サマリー */}
        <div
          className={`px-4 py-2 text-sm border-b ${
            isDarkMode
              ? "text-gray-400 bg-gray-800 border-gray-700"
              : "text-gray-500 bg-gray-50"
          }`}
        >
          {totalTasks}タスク・{totalReminders}件のリマインダー
        </div>

        {/* メインコンテンツ */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div
              className={`p-8 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              読み込み中...
            </div>
          ) : totalTasks === 0 ? (
            <div
              className={`p-8 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              リマインダーが設定されているタスクはありません
            </div>
          ) : (
            <>
              {/* 現在のプロジェクト */}
              {currentProjectTasks.length > 0 && (
                <ProjectSection
                  projectId={-1}
                  projectName={currentTodoListName}
                  tasks={currentProjectTasks}
                  isExpanded={expandedProjects.has(-1)}
                  onToggle={() => toggleProjectExpand(-1)}
                />
              )}

              {/* 他のプロジェクト */}
              {otherProjects.map((project) => (
                <ProjectSection
                  key={project.todoListId}
                  projectId={project.todoListId}
                  projectName={project.todoListName}
                  tasks={project.tasks}
                  isExpanded={expandedProjects.has(project.todoListId)}
                  onToggle={() => toggleProjectExpand(project.todoListId)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
